import { useLayoutEffect, useRef, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { Box, FormHelperText, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NOTE_INPUT_DEFAULT_CHARACTER_LIMIT } from '@/data/constants';

const PREFIX = 'NoteInputWithVisibleThreshold';

const classes = {
  container: `${PREFIX}-container`,
  hiddenInput: `${PREFIX}-hidden-input`,
  editable: `${PREFIX}-editable`,
  editableFloating: `${PREFIX}-editable-floating`,
  boldText: `${PREFIX}-bold-text`,
  warning: `${PREFIX}-warning`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    position: 'relative',
  },

  [`& .${classes.editable}`]: {
    position: 'absolute',
    top: 1,
    left: 1,
    right: 1,
    bottom: 1,
    color: theme.palette.text.primary,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    outline: 'none',
    whiteSpace: 'pre',
    overflowX: 'auto',
    overflowY: 'hidden',
    zIndex: 2,
    cursor: 'text',
    padding: '12px',
  },

  [`& .${classes.editable}.${classes.editableFloating}`]: {
    padding: '20px 12px 4px',
  },

  [`& .${classes.hiddenInput} .MuiInputBase-input`]: {
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    caretColor: 'transparent',
    pointerEvents: 'none',
    userSelect: 'none',
  },

  [`& .${classes.hiddenInput} .MuiOutlinedInput-root`]: {
    backgroundColor: theme.palette.common.white,
  },

  [`& .${classes.hiddenInput} .MuiInputBase-root.MuiInputBase-readOnly`]: {
    backgroundColor: theme.palette.common.white,
  },

  [`& .${classes.boldText}`]: {
    fontWeight: 700,
  },
}));

const StyledHiddenInput = styled(TextField)({
  width: '100%',
});

const StyledWarningHelperText = styled(FormHelperText)(({ theme }) => ({
  [`&.${classes.warning}`]: {
    color: `${theme.palette.warning.main} !important`,
  },
}));

interface NoteInputWithVisibleThresholdProps {
  control: Control<any>;
  label: string;
  name: string;
  warningMessage: string;
  characterLimit?: number;
}

const sanitizeNoteValue = (value: string) => value.replace(/\r?\n/g, ' ');

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getStyledNoteHtml = (value: string, characterLimit: number) => {
  const boldText = escapeHtml(value.slice(0, characterLimit));
  const plainText = escapeHtml(value.slice(characterLimit));

  return `<span class="${classes.boldText}">${boldText}</span><span>${plainText}</span>`;
};

const getCaretCharacterOffset = (container: HTMLElement): number | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const activeRange = selection.getRangeAt(0);
  if (!container.contains(activeRange.startContainer)) return null;

  const preCaretRange = activeRange.cloneRange();
  preCaretRange.selectNodeContents(container);
  preCaretRange.setEnd(activeRange.startContainer, activeRange.startOffset);
  return preCaretRange.toString().length;
};

const setCaretCharacterOffset = (container: HTMLElement, offset: number) => {
  const selection = window.getSelection();
  if (!selection) return;

  const safeOffset = Math.max(0, offset);
  const range = document.createRange();
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let traversed = 0;
  let textNode = walker.nextNode();

  while (textNode) {
    const textLength = textNode.textContent?.length ?? 0;
    if (traversed + textLength >= safeOffset) {
      range.setStart(textNode, safeOffset - traversed);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }

    traversed += textLength;
    textNode = walker.nextNode();
  }

  range.selectNodeContents(container);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

export default function NoteInputWithVisibleThreshold({
  control,
  label,
  name,
  warningMessage,
  characterLimit = NOTE_INPUT_DEFAULT_CHARACTER_LIMIT,
}: NoteInputWithVisibleThresholdProps) {
  const { field, fieldState } = useController({
    name: name as any,
    control,
    defaultValue: '',
  });
  const editorRef = useRef<HTMLDivElement | null>(null);
  const pendingCaretOffsetRef = useRef<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const value = typeof field.value === 'string' ? field.value : '';
  const isOverProviderVisibleLimit = value.length > characterLimit;
  const helperText = fieldState.error?.message
    ? fieldState.error.message
    : isOverProviderVisibleLimit
      ? warningMessage
      : undefined;
  const usesFloatingLabelPadding = isFocused || value.length > 0;

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const nextHtml = getStyledNoteHtml(value, characterLimit);
    if (editor.innerHTML !== nextHtml) {
      editor.innerHTML = nextHtml;
    }

    if (isFocused && pendingCaretOffsetRef.current !== null) {
      setCaretCharacterOffset(
        editor,
        Math.min(pendingCaretOffsetRef.current, value.length),
      );
    }

    pendingCaretOffsetRef.current = null;
  }, [characterLimit, isFocused, value]);

  return (
    <>
      <StyledBox className={classes.container}>
        <StyledHiddenInput
          className={classes.hiddenInput}
          name={field.name}
          value={value}
          label={label}
          variant="outlined"
          error={Boolean(fieldState.error)}
          focused={isFocused}
          inputProps={{
            tabIndex: -1,
            'aria-hidden': true,
          }}
        />
        <Box
          ref={(node) => {
            editorRef.current = node as HTMLDivElement | null;
            field.ref(node);
          }}
          className={`${classes.editable} ${usesFloatingLabelPadding ? classes.editableFloating : ''}`}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
          aria-invalid={Boolean(fieldState.error)}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
            field.onBlur();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          onInput={(event) => {
            const updatedValue = sanitizeNoteValue(
              event.currentTarget.textContent ?? '',
            );
            const caretOffset = getCaretCharacterOffset(event.currentTarget);
            pendingCaretOffsetRef.current =
              caretOffset === null
                ? updatedValue.length
                : Math.min(caretOffset, updatedValue.length);
            field.onChange(updatedValue);
          }}
        />
      </StyledBox>
      {helperText && (
        <StyledWarningHelperText
          error={Boolean(fieldState.error)}
          className={
            !fieldState.error && isOverProviderVisibleLimit
              ? classes.warning
              : undefined
          }>
          {helperText}
        </StyledWarningHelperText>
      )}
    </>
  );
}
